import React, { useState, useEffect } from 'react';
import { Check, X, Edit2, Trash2, Search, Filter } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { Location } from '../types';
import { EditLocation } from '../components/EditLocation';
import { getLocations } from '../utils/api';
import { updateLocation, deleteLocation } from '../utils/supabase';

// Get PIN from environment variables
const ADMIN_PIN = import.meta.env.VITE_ADMIN_PIN || '0890';

interface AdminFilters {
  status: 'all' | 'pending' | 'approved' | 'rejected';
  category: string;
  searchQuery: string;
}

export const AdminPanel: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pinCode, setPinCode] = useState('');
  const [locations, setLocations] = useState<Location[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [filters, setFilters] = useState<AdminFilters>({
    status: 'pending',
    category: 'all',
    searchQuery: '',
  });
  const [expandedLocation, setExpandedLocation] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchLocations();
    }
  }, [isAuthenticated]);

  const fetchLocations = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { locations: data } = await getLocations(true); // Pass true to indicate admin view
      setLocations(data || []);
    } catch (err) {
      setError('Не атрымалася загрузіць лакацыі: ' + (err instanceof Error ? err.message : 'Невядомая памылка'));
      toast.error('Не атрымалася загрузіць лакацыі');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Simple PIN comparison (in production, you might want to hash this)
    if (pinCode === ADMIN_PIN) {
      setIsAuthenticated(true);
      setError(null);
      toast.success('Паспяховы ўваход');
    } else {
      setError('Няправільны PIN-код');
      toast.error('Няправільны PIN-код');
    }
  };

  const handleStatusChange = async (id: string, newStatus: 'approved' | 'rejected') => {
    try {
      setError(null);
      const location = locations.find(loc => loc.id === id);
      if (!location) {
        throw new Error('Location not found');
      }

      const { error: updateError } = await updateLocation({
        ...location,
        status: newStatus
      });

      if (updateError) {
        throw new Error(updateError);
      }

      setLocations(prevLocations => 
        prevLocations.map(loc => 
          loc.id === id ? { ...loc, status: newStatus } : loc
        )
      );

      toast.success(newStatus === 'approved' ? 'Лакацыя адобрана' : 'Лакацыя адхілена');
      await fetchLocations();
    } catch (err) {
      const errorMessage = 'Не атрымалася змяніць статус: ' + (err instanceof Error ? err.message : 'Невядомая памылка');
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id: string) => {
    const confirmDelete = window.confirm('Вы ўпэўнены, што жадаеце выдаліць гэтую лакацыю?');
    if (!confirmDelete) return;

    try {
      setError(null);
      const { error: deleteError } = await deleteLocation(id);

      if (deleteError) {
        throw new Error(deleteError);
      }

      setLocations(prevLocations => prevLocations.filter(loc => loc.id !== id));
      toast.success('Лакацыя паспяхова выдалена');
      await fetchLocations();
    } catch (err) {
      const errorMessage = 'Не атрымалася выдаліць лакацыю: ' + (err instanceof Error ? err.message : 'Невядомая памылка');
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleEdit = (location: Location) => {
    setSelectedLocation(location);
  };

  const handleSave = async (updatedLocation: Location) => {
    try {
      setError(null);
      const { error: updateError } = await updateLocation(updatedLocation);

      if (updateError) {
        throw new Error(updateError);
      }

      setLocations(prevLocations =>
        prevLocations.map(loc =>
          loc.id === updatedLocation.id ? updatedLocation : loc
        )
      );

      setSelectedLocation(null);
      toast.success('Лакацыя паспяхова абноўлена');
      await fetchLocations();
    } catch (err) {
      const errorMessage = 'Не атрымалася абнавіць лакацыю: ' + (err instanceof Error ? err.message : 'Невядомая памылка');
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const filteredLocations = locations.filter(location => {
    if (filters.status !== 'all' && location.status !== filters.status) {
      return false;
    }
    if (filters.category !== 'all' && location.category !== filters.category) {
      return false;
    }
    if (filters.searchQuery) {
      const searchLower = filters.searchQuery.toLowerCase();
      return (
        location.name.toLowerCase().includes(searchLower) ||
        location.description.toLowerCase().includes(searchLower) ||
        location.shortDescription.toLowerCase().includes(searchLower) ||
        (location.tags && location.tags.some(tag => tag.toLowerCase().includes(searchLower)))
      );
    }
    return true;
  });

  const categories = Array.from(new Set(locations.map(l => l.category)));

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Toaster position="top-center" />
        <div className="bg-white rounded-2xl p-8 shadow-lg max-w-md w-full">
          <h1 className="text-2xl font-bold text-center mb-6">Панэль адміністратара</h1>
          <form onSubmit={handlePinSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PIN-код
              </label>
              <input
                type="password"
                value={pinCode}
                onChange={(e) => setPinCode(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                placeholder="Увядзіце PIN-код"
              />
            </div>
            {error && (
              <p className="text-red-500 text-sm">{error}</p>
            )}
            <button
              type="submit"
              className="w-full bg-primary text-white py-2 rounded-lg hover:bg-primary/90"
            >
              Увайсці
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <Toaster position="top-center" />
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">Кіраванне лакацыямі</h1>
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <p>{error}</p>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Пошук..."
                value={filters.searchQuery}
                onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
              />
            </div>
            
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as AdminFilters['status'] }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary appearance-none"
              >
                <option value="all">Усе статусы</option>
                <option value="pending">На разглядзе</option>
                <option value="approved">Адобраныя</option>
                <option value="rejected">Адхіленыя</option>
              </select>
            </div>

            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <select
                value={filters.category}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary appearance-none"
              >
                <option value="all">Усе катэгорыі</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">
              Загрузка...
            </div>
          ) : filteredLocations.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Лакацыі не знойдзены
            </div>
          ) : (
            filteredLocations.map((location) => (
              <div
                key={location.id}
                className="bg-white rounded-xl shadow-sm overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Image Gallery */}
                    <div className="w-full md:w-1/3">
                      <div className="aspect-video rounded-lg overflow-hidden bg-gray-100">
                        {location.images && location.images.length > 0 ? (
                          <img
                            src={location.images[0]}
                            alt={location.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            Няма фотаздымкаў
                          </div>
                        )}
                      </div>
                      {location.images && location.images.length > 1 && (
                        <div className="grid grid-cols-4 gap-2 mt-2">
                          {location.images.slice(1, 5).map((image, index) => (
                            <div key={index} className="aspect-square rounded-lg overflow-hidden">
                              <img
                                src={image}
                                alt={`${location.name} - ${index + 2}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-semibold">{location.name}</h3>
                          <span className={`inline-block px-3 py-1 text-sm rounded-full mt-2 ${
                            location.status === 'approved' 
                              ? 'bg-green-100 text-green-800'
                              : location.status === 'rejected'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {location.status === 'approved' ? 'Адобрана' : 
                             location.status === 'rejected' ? 'Адхілена' : 
                             'На разглядзе'}
                          </span>
                        </div>

                        <div className="flex gap-2">
                          {location.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleStatusChange(location.id, 'approved')}
                                className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-full transition-colors"
                                title="Адобрыць"
                              >
                                <Check size={20} />
                              </button>
                              <button
                                onClick={() => handleStatusChange(location.id, 'rejected')}
                                className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-full transition-colors"
                                title="Адхіліць"
                              >
                                <X size={20} />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleEdit(location)}
                            className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-full transition-colors"
                            title="Рэдагаваць"
                          >
                            <Edit2 size={20} />
                          </button>
                          <button
                            onClick={() => handleDelete(location.id)}
                            className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-full transition-colors"
                            title="Выдаліць"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>

                      <p className="text-gray-600 mb-4">{location.shortDescription}</p>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {location.tags?.map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-1 bg-gray-100 rounded-full text-sm text-gray-600"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                        <div>
                          <p><strong>Катэгорыя:</strong> {location.category}</p>
                          <p><strong>Дата стварэння:</strong> {new Date(location.created_at!).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p><strong>Каардынаты:</strong> {location.coordinates.lat}, {location.coordinates.lng}</p>
                          <p><strong>Апошняе абнаўленне:</strong> {new Date(location.updated_at!).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {selectedLocation && (
        <EditLocation
          location={selectedLocation}
          onSave={handleSave}
          onClose={() => setSelectedLocation(null)}
          isAdmin={true}
        />
      )}
    </div>
  );
};